import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  FileText, 
  Receipt,
  CreditCard,
  Landmark, 
  Users, 
  Package, 
  FolderTree,
  Building2,
  ChevronDown,
  ChevronRight,
  Wallet,
  FileBarChart,
  UserCircle,
  BadgeDollarSign,
  ClipboardList,
  Tags,
  Store,
  Menu,
  X,
  ArrowRightLeft,
  History,
  LogOut,
  Shield,
  Settings,
  Target,
  Home,
  TrendingUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';

const menuItems = [
  {
    title: 'Inicio',
    icon: Home,
    path: '/',
    isHome: true
  },
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/finanzas'
  },
  {
    title: 'Ventas',
    icon: Store,
    children: [
      { title: 'Ventas POS', icon: Store, path: '/finanzas/ventas' },
      { title: 'Créditos por Cobrar', icon: CreditCard, path: '/finanzas/creditos' },
    ]
  },
  {
    title: 'Presupuestos',
    icon: Target,
    path: '/finanzas/presupuestos'
  },
  {
    title: 'Proveedores y Egresos',
    icon: ShoppingCart,
    children: [
      { title: 'Órdenes de Compra', icon: ClipboardList, path: '/finanzas/ordenes-compra' },
      { title: 'Factura Proveedor', icon: FileText, path: '/finanzas/compras/factura' },
      { title: 'Gastos', icon: Receipt, path: '/finanzas/compras/gasto' },
      { title: 'Pagar Facturas', icon: CreditCard, path: '/finanzas/pagar-facturas' },
      { title: 'CxP Pendientes', icon: Wallet, path: '/finanzas/cxp' },
      { title: 'Letras', icon: FileBarChart, path: '/finanzas/letras' },
    ]
  },
  {
    title: 'Bancos y Pagos',
    icon: Landmark,
    children: [
      { title: 'Cuentas Bancarias', icon: Landmark, path: '/finanzas/cuentas' },
      { title: 'Movimientos/Pagos', icon: BadgeDollarSign, path: '/finanzas/pagos' },
      { title: 'Conciliación Bancaria', icon: ArrowRightLeft, path: '/finanzas/conciliacion' },
      { title: 'Historial Conciliaciones', icon: History, path: '/finanzas/historial-conciliaciones' },
    ]
  },
  {
    title: 'Planilla',
    icon: Users,
    children: [
      { title: 'Empleados', icon: Users, path: '/finanzas/empleados' },
      { title: 'Adelantos', icon: BadgeDollarSign, path: '/finanzas/adelantos' },
      { title: 'Generar Planilla', icon: FileBarChart, path: '/finanzas/planilla' },
    ]
  },
  {
    title: 'Catálogos',
    icon: FolderTree,
    children: [
      { title: 'Empresas', icon: Building2, path: '/finanzas/empresas' },
      { title: 'Proveedores', icon: Users, path: '/finanzas/proveedores' },
      { title: 'Clientes', icon: UserCircle, path: '/finanzas/clientes' },
      { title: 'Artículos', icon: Package, path: '/finanzas/articulos' },
      { title: 'Categorías', icon: Tags, path: '/finanzas/categorias' },
      { title: 'Líneas de Negocio', icon: Store, path: '/finanzas/lineas-negocio' },
      { title: 'Centros de Costo', icon: FolderTree, path: '/finanzas/centros-costo' },
    ]
  },
  {
    title: 'Contabilidad',
    icon: TrendingUp,
    children: [
      { title: 'Plan de Cuentas', icon: FolderTree, path: '/finanzas/cuentas-contables' },
      { title: 'Balance General', icon: FileBarChart, path: '/finanzas/balance-general' },
    ]
  },
  {
    title: 'Reportes',
    icon: FileBarChart,
    path: '/finanzas/reportes'
  },
  {
    title: 'Integración',
    icon: ArrowRightLeft,
    path: '/finanzas/integracion'
  }
];

// Admin menu items
const adminMenuItems = [
  {
    title: 'Administración',
    icon: Settings,
    adminOnly: true,
    children: [
      { title: 'Usuarios', icon: Users, path: '/finanzas/usuarios' },
      { title: 'Historial Actividad', icon: History, path: '/finanzas/historial-actividad' },
      { title: 'Copias de Seguridad', icon: Shield, path: '/finanzas/backups' },
    ]
  }
];

const MenuItem = ({ item, isOpen, toggleOpen, isMobile, onNavigate, userRole }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path === location.pathname || 
    (hasChildren && item.children.some(child => child.path === location.pathname));
  
  // Check if item is admin only
  if (item.adminOnly && userRole !== 'ADMIN') {
    return null;
  }
  
  const Icon = item.icon;

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={toggleOpen}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all",
            isActive 
              ? "bg-emerald-50 text-emerald-700 font-medium" 
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <span>{item.title}</span>
          </div>
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {isOpen && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = child.path === location.pathname;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={isMobile ? onNavigate : undefined}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all",
                    isChildActive 
                      ? "bg-emerald-100 text-emerald-700 font-medium" 
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <ChildIcon className="w-4 h-4" />
                  <span>{child.title}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Special styling for Home button
  if (item.isHome) {
    return (
      <Link
        to={item.path}
        onClick={isMobile ? onNavigate : undefined}
        className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all mb-3 border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
      >
        <Icon className="w-5 h-5" />
        <span>{item.title}</span>
      </Link>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={isMobile ? onNavigate : undefined}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all mb-1",
        isActive 
          ? "bg-emerald-100 text-emerald-700 font-medium" 
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      )}
    >
      <Icon className="w-5 h-5" />
      <span>{item.title}</span>
    </Link>
  );
};

export const Layout = ({ children }) => {
  const { empresaActual, empresas, setEmpresaActual, tcHoy, user, logout } = useApp();
  const [openMenus, setOpenMenus] = useState({ 'Proveedores y Egresos': true });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = (title) => {
    setOpenMenus(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user?.nombre) return 'U';
    const parts = user.nombre.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.nombre[0].toUpperCase();
  };

  // All menu items combined
  const allMenuItems = [...menuItems, ...adminMenuItems];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30 h-14">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="font-semibold text-gray-800 hidden sm:inline">FinTrack</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* TC Display */}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
              <span>TC:</span>
              <span className="font-medium text-gray-700">{tcHoy.tc_venta?.toFixed(2)}</span>
            </div>
            
            {/* Empresa Selector */}
            {empresas.length > 0 && (
              <Select
                value={empresaActual?.id?.toString() || ''}
                onValueChange={(value) => {
                  const emp = empresas.find(e => e.id.toString() === value);
                  if (emp) setEmpresaActual(emp);
                }}
              >
                <SelectTrigger className="w-[180px] md:w-[220px] h-9">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map(emp => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-indigo-600 text-white text-sm">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center gap-2 p-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-indigo-600 text-white text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{user?.nombre || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">{user?.rol || 'USUARIO'}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {user?.rol === 'ADMIN' && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/usuarios')}>
                      <Users className="w-4 h-4 mr-2" />
                      Gestión de Usuarios
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/historial-actividad')}>
                      <History className="w-4 h-4 mr-2" />
                      Historial de Actividad
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-14 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transition-transform duration-300",
        "lg:translate-x-0",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <nav className="p-4 h-full overflow-y-auto">
          {allMenuItems.map((item) => (
            <MenuItem
              key={item.title}
              item={item}
              isOpen={openMenus[item.title]}
              toggleOpen={() => toggleMenu(item.title)}
              isMobile={mobileMenuOpen}
              onNavigate={closeMobileMenu}
              userRole={user?.rol}
            />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 min-h-screen">
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
